import WixBaseEvent from '../events/WixBaseEvent';

/**
 * @callback $w.EventHandler
 * @description Handles events.
 * @param {$w.Event} event The event that occurred.
 * @param {$w.$w} $w Deprecated: A selector function. The $w function was used to
 *  enable event handlers to work with elements in [repeaters]($w.Repeater.html). Now, to get
 *  a scoped selector for working with repeater items, use the [$w.at()]($w.html#at) function
 *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`.
 */

/**
 * @class Event
 * @summary Events are fired when certain actions occur to elements.
 * @description
 *  Events can be caused by user interactions, such as clicking the mouse or
 *  pressing a key, or generated programmatically, such as hiding or showing an
 *  element using the [hide()]($w.HiddenMixin.html#hide) and
 *  [show()]($w.HiddenMixin.html#hide) functions.
 * 
 *  Because the functionality provided by `Event` can be inherited and used when actions 
 *  occur to elements, it is considered a "mixin."
 * 
 *  **About Mixins**
 * 
 *  [Mixins](https://support.wix.com/en/article/velo-about-mixins) provide functionality that 
 *  other elements and event handlers can inherit and use. 
 *  
 *  Mixins are not elements. You cannot add mixins to a page in the Editor like other `$w` elements, 
 *  and mixins are not meant to be used directly in your code. 
 *  
 *  For example, you would not write code like this, because it is out of context: 
 * 
 *  ```javascript
 *  $w("#Event").onEvent( (event) => {
 *     let eventType = event.type;
 *  } );
 *  ```
 * 
 *  Instead, you can code the following if `myElement` has an event handler that 
 *  "mixes in" `Event`. 
 * 
 *  ```javascript
 *  $w("#myElement").onEvent( (event) => {
 *     let eventType = event.type;
 *  } );
 *  ```
 *  
 * @memberof $w
 */
class WixEvent extends WixBaseEvent{
    constructor(target, event) {
        super(event);
        this.target = target;
        this.syntheticEvent = event;
    }

    /**
     * @member {$w.Element} target
     * @label property
     * @summary Gets the element that the event was fired on.
     * @snippet [Event-target.es6=Get the ID of the target element]
     * @memberof $w.Event
     * @readonly
     */

    /**
     * @member {String} type
     * @label property
     * @summary Gets the type of event that was fired.
     * @snippet [Event-type.es6=Get the type of the event]
     * @memberof $w.Event
     * @readonly
     */

    /**
     * @typedef {Object} $w.Event~EventContext
     * @summary An object that contains information about the context in which an event was fired.
     * @property {String} type `"GLOBAL_SCOPE"` for events fired outside
     *  of repeaters, or `"COMPONENT_SCOPE"` for events fired from repeaters.
     * @property {String} itemId ID of the repeater item where the
     *  event was fired from.
     * @snippet [Event-context.es6=Get the context of the event]
     * @see [context](#context)
     * @memberof $w.Event
     */

    /**
     * @member {$w.Event~EventContext} context
     * @member {Object} context
     * @label property
     * @summary Gets the context in which an event was fired.
     * @description
     *  An event's `context` contains information about the circumstances
     *  surrounding the firing of the event.
     *  The context property only appears in events on repeated elements.
     *
     *  You can use the event context with [`$w.at()`]($w.html#at) to get a
     *  selector function which selects items from a specific repeater item.
     *  It contains an object with one key:value pair. The key is "itemId" and
     *  the value is the ID of the repeated item on which the event occurred.
     * @snippet [Event-context.es6=Get the context of the event]
     * @memberof $w.Event
     * @readonly
     */

}

export default WixEvent;
